<template>
  <v-dialog 
    v-model="dialog" 
    scrollable
    persistent 
    max-width="1200px">
    <v-fab-transition 
      v-if="placement['.key']" 
      slot="activator">
      <v-btn 
        slot="activator" 
        fab
        dark
        small
        absolute
        bottom
        right
        color="secondary">
        <v-icon dark>edit</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-fab-transition 
      v-else 
      slot="activator">
      <v-btn
        slot="activator" 
        color="secondary"
        fab
        dark
        absolute
        bottom            
        right
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-form 
      ref="form" 
      v-model="valid"                 
      lazy-validation>
      <v-card>
        <v-toolbar 
          color="primary" 
          dark>
          <v-toolbar-title>
            <span class="headline">Update placement</span>
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout 
              row 
              wrap>
              <v-flex 
                xs12 
                sm8 
                offset-lg2>
                <v-alert 
                  :value="true" 
                  type="info">
                  A new placement needs to be created to change the unit.
                </v-alert>
              </v-flex>
              <v-flex 
                xs12 
                sm6 
                lg4 
                offset-lg2>
                <v-select
                  v-model="placement.campus"
                  :rules="[v => !!v || 'Campus is required']"
                  :items="['Burwood', 'Waterfront', 'Warrnambool', 'Waurn Ponds', 'Cloud']"
                  label="Campus"                          
                  required                          
                />
              </v-flex>
              <v-flex 
                xs12 
                sm6 
                lg4>
                <v-select
                  :items="organisations"
                  :rules="[v => !!v || 'Organisation is required']"
                  v-model="selectedOrganisation"
                  label="Oraganisation / Venue"
                  required
                  return-object                         
                >
                  <template 
                    slot="selection" 
                    slot-scope="data">
                    {{ data.item.fullname }}
                  </template>
                  <template 
                    slot="item" 
                    slot-scope="data">
                    <v-list-tile-content>
                      <v-list-tile-title v-html="data.item.fullname"/>
                    </v-list-tile-content>
                  </template>
                </v-select>
              </v-flex>
            </v-layout>            
            <v-layout 
              row 
              wrap>
              <v-flex 
                xs12 
                sm6 
                lg4 
                offset-lg2>
                <v-text-field
                  v-model="placement.studentId"
                  :rules="[
                    v => !!v || 'ID is required',
                    v => !v || v.length <= 14 || 'ID must be under 15 characters',
                    v => !v || v.length >= 5 || 'ID must be at least 5 characters',
                    v => /^\d{5,14}$/.test(v) || 'ID expects numbers only'
                  ]"
                  label="Student ID"
                  required
                />
              </v-flex>              
            </v-layout>
            <v-layout 
              row 
              wrap>
              <v-flex 
                xs12 
                md6 
                lg4 
                offset-lg2>
                <v-menu
                  ref="menuStartDate"
                  :close-on-content-click="false"
                  v-model="menuStartDate"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    :value="computedStartDate"
                    :rules="[v => !!v || 'Start date is required']"
                    label="Start date"
                    prepend-icon="event"
                    readonly
                  />
                  <v-date-picker 
                    v-model="placement.startDate"
                    color="green lighten-1"
                    no-title
                    @input="$refs.menuStartDate.save(placement.startDate)"/>
                </v-menu>
              </v-flex>
              <v-flex 
                xs12 
                md6 
                lg4>
                <v-menu
                  ref="menuEndDate"
                  :close-on-content-click="false"
                  v-model="menuEndDate"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    :value="computedEndDate"
                    :rules="[v => !!v || 'End date is required']"
                    label="End date"
                    prepend-icon="event"
                    readonly
                  />
                  <v-date-picker 
                    v-model="placement.endDate"
                    color="cyan darken-1"
                    no-title
                    @input="$refs.menuEndDate.save(placement.endDate)"/>
                </v-menu>
              </v-flex>
            </v-layout>
            <v-flex 
              xs12 
              lg8 
              offset-lg2>
              <v-layout 
                v-for="(agreement, index) in agreements" 
                :key="index" 
                align-center                         
                class="mt-3">
                <v-checkbox
                  :rules="[v => !!v || 'You must agree to continue!']"
                  v-model="agreement.value"
                  hide-details
                  class="shrink mr-2"
                  required
                  color="success"
                />
                <label v-html="agreement.html"/>
              </v-layout>
            </v-flex>
          </v-container>          
        </v-card-text>
        <v-progress-linear 
          v-if="loading"
          :indeterminate="loading"/>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn 
            color="primary" 
            flat 
            @click.native="close">Close</v-btn>
          <v-btn 
            v-if="placement['.key']"
            color="red darken-1" 
            flat 
            @click.native="deleteDialog = true">Delete</v-btn>
          <v-btn 
            v-if="placement['.key']"
            color="primary" 
            flat 
            @click.native="update(placement)">Update</v-btn>
        </v-card-actions>      
      </v-card>
    </v-form>
    <v-dialog 
      v-model="deleteDialog" 
      persistent 
      max-width="290">
      <v-card>
        <v-card-title class="headline">Delete placement?</v-card-title>
        <v-alert 
          :value="true" 
          error>This placement can only be deleted by a Staff or Academic with Admin rights.</v-alert>
        <v-card-actions>
          <v-spacer/>
          <v-btn 
            color="green darken-1" 
            flat 
            @click="deleteDialog = false">Close</v-btn>
          <v-btn 
            color="red darken-1" 
            flat 
            @click="remove(placement)">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
export default {
  name: "EditForm",
  props: {
    edititem: {
      type: [Array, Object],
      default: null
    }
  },
  data() {
    return {
      dialog: false,
      deleteDialog: false,
      confirm: false,
      menuStartDate: false,
      menuEndDate: false,
      valid: true,
      nameRules: [
        v => !!v || "Name is required",
        v => (v && v.length < 100) || "Name must be less than 100 characters"
      ],
      codeRules: [
        v => !!v || "Code is required",
        v => (v && v.length !== 5) || "Code must be 6 or 7 characters",
        v =>
          /^([A-Z]{3}[0-9]{3}[a-z]?)+$/.test(v) ||
          'Expecting 3 capitalised letters followed by 3 numbers. ("HNN300" / "HNN300b")'
      ],
      selectedCourse: null,
      selectedUnit: null,
      selectedOrganisation: null,
      organisations: [],
      courses: [],
      units: [],
      agreements: [],
      placement: JSON.parse(JSON.stringify(this.edititem))
    };
  },
  computed: {
    computedStartDate() {
      return this.formatDate(this.placement.startDate);
    },
    computedEndDate() {
      return this.formatDate(this.placement.endDate);
    },
    user() {
      return this.$store.getters.user;
    },
    loading() {
      return this.$store.getters.loading;
    }
  },
  firestore() {
    return {
      courses: this.$store.getters.db.collection("courses").orderBy("code"),
      organisations: this.$store.getters.db
        .collection("roles/organisation/users")
        .orderBy("fullname")
    };
  },
  watch: {
    organisations: function(val) {
      let self = this;
      if (this.placement[".key"]) {
        val.forEach(function(o) {
          if (o.username === self.placement.organisation) {
            self.selectedOrganisation = o;
          }
        });
      }
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    remove(placement) {
      console.log("Delete", placement);
      let self = this;
      this.deleteDialog = false;
      this.dialog = false;
      this.$store.dispatch("setLoading", true);
      this.$store.getters.db
        .collection("students")
        .doc(placement.uid)
        .collection("placements")
        .doc(placement[".key"])
        .delete()
        .then(ref => {
          self.$store.dispatch("sendNotification", {
            snackbar: true,
            color: "cyan darken-2",
            text: placement.unit + " placement deleted"
          });
          self.$store.dispatch("setLoading", false);
          self.$router.push({ path: self.$route.matched[0].path });
        })
        .catch(err => {
          console.log(err);
          self.$store.dispatch("sendNotification", {
            snackbar: true,
            color: "error",
            text: "Error occured deleting placement. Contact Health app support"
          });
          self.$refs.form.reset();
          self.$store.dispatch("setLoading", false);
          self.$router.push({ path: self.$route.matched[0].path });
        });
    },
    update(placement) {
      if (this.$refs.form.validate()) {
        let self = this;
        this.dialog = false;
        let updatePlacement = JSON.parse(JSON.stringify(placement));
        this.$store.dispatch("setLoading", true);
        delete updatePlacement[".key"];
        delete updatePlacement.createdAt;
        updatePlacement.lastUpdate = new Date();
        updatePlacement.organisation = this.selectedOrganisation.username;
        updatePlacement.organisationName = this.selectedOrganisation.fullname;
        this.$store.getters.db
          .collection("students")
          .doc(placement.uid)
          .collection("placements")
          .doc(placement[".key"])
          .update(updatePlacement)
          .then(ref => {
            self.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "cyan darken-2",
              text: placement.unit + " placement updated"
            });
            self.$store.dispatch("setLoading", false);
          })
          .catch(err => {
            self.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "error",
              text: "Error occured updating placement"
            });
            self.$store.dispatch("setLoading", false);
            console.log(err);
          });
      }
    },
    close() {
      this.$store.dispatch("sendNotification", {
        snackbar: true,
        color: "red darken-2",
        text: "Closed (not saved)"
      });
      this.$refs.form.reset();
      this.dialog = false;
    },
    loadUnits() {
      if (this.selectedCourse) {
        let self = this;
        this.$store.dispatch("setLoading", true);
        this.$binding(
          "units",
          this.$store.getters.db
            .collection("courses")
            .doc(this.selectedCourse[".key"])
            .collection("units")
        ).then(units => {
          self.$store.dispatch("setLoading", false);
        });
      } else {
        this.units = [];
      }
    },
    loadAgreements() {
      if (this.selectedUnit) {
        console.log("loading agreements");
        let self = this;
        this.$store.dispatch("setLoading", true);
        this.$binding(
          "agreements",
          this.$store.getters.db
            .collection("courses")
            .doc(this.selectedCourse[".key"])
            .collection("units")
            .doc(this.selectedUnit[".key"])
            .collection("agreements")
        ).then(agreements => {
          self.$store.dispatch("setLoading", false);
        });
      } else {
        this.units = [];
      }
    }
  }
};
</script>
